'use client';

import { useState, type ReactNode } from 'react';
import { Link as LinkIcon, Unlink } from 'lucide-react';
import type { GalleryBlock } from '@impact/types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button/button';
import { Input } from '@/components/ui/input/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover/popover';
import { EditableMediaCell } from './_editing/EditableMediaCell';
import { usePageEditorStore } from '@/components/page-editor/v2/store/usePageEditor';

interface GalleryBlockRendererProps {
  block: GalleryBlock;
  editing?: boolean;
}

const GAP_CLASSES = {
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
} as const;

const ASPECT_CLASSES = {
  auto: '',
  video: 'aspect-video',
  square: 'aspect-square',
  '4/3': 'aspect-[4/3]',
} as const;

const COLUMN_GRID_CLASSES: Record<2 | 3 | 4, string> = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
};

const COLUMN_MASONRY_CLASSES: Record<2 | 3 | 4, string> = {
  2: 'columns-1 sm:columns-2',
  3: 'columns-1 sm:columns-2 lg:columns-3',
  4: 'columns-2 lg:columns-4',
};

const BUTTON_STYLE_CLASSES = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
} as const;

export function GalleryBlockRenderer({ block, editing }: GalleryBlockRendererProps) {
  const {
    images,
    columns = 3,
    gap = 'md',
    aspectRatio = '4/3',
    rounded = true,
    shadow = false,
    showCard = false,
    showButton = false,
    buttonText = 'Download',
    buttonStyle = 'primary',
    showCaptions = true,
    captionStyle = 'below',
    lightbox = false,
    variant = 'default',
  } = block.data;

  if (!editing && (!images || images.length === 0)) return null;

  const safeImages = images ?? [];
  const isMasonry = variant === 'masonry';
  const isMosaic = variant === 'mosaic';
  const isMarquee = variant === 'marquee';
  const isAutoAspect = aspectRatio === 'auto' || isMasonry;

  const renderCellMedia = (image: GalleryBlock['data']['images'][number], i: number) => {
    if (editing) {
      return (
        <EditableImageCell
          blockId={block.id}
          index={i}
          src={image.src}
          alt={image.alt}
          isAutoAspect={isAutoAspect}
        />
      );
    }
    if (!image.src) return null;
    /* eslint-disable-next-line @next/next/no-img-element */
    return (
      <img
        src={image.src}
        alt={image.alt}
        className={cn(
          isAutoAspect ? 'h-auto w-full' : 'absolute inset-0 h-full w-full object-cover',
        )}
      />
    );
  };

  const wrapWithLink = (image: GalleryBlock['data']['images'][number], children: ReactNode) => {
    if (editing) return children;
    if (image.linkUrl) {
      return (
        <a
          href={image.linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {children}
        </a>
      );
    }
    return children;
  };

  const cellContent = (image: GalleryBlock['data']['images'][number], i: number) => (
    <figure
      className={cn(
        'group relative overflow-hidden',
        showCard && 'border border-border/40 bg-card',
        rounded && 'rounded-xl',
        shadow && 'shadow-lg',
        isMasonry && 'mb-4 break-inside-avoid',
      )}
      {...(editing ? { 'data-block-item': String(i) } : {})}
    >
      <div
        className={cn('relative overflow-hidden', isAutoAspect ? '' : ASPECT_CLASSES[aspectRatio])}
      >
        {wrapWithLink(image, renderCellMedia(image, i))}
        {captionStyle === 'overlay' && image.caption ? (
          <figcaption
            className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2 text-sm text-white opacity-0 transition-opacity group-hover:opacity-100"
            {...(editing ? { 'data-editable': `data.images[${i}].caption` } : {})}
          >
            {image.caption}
          </figcaption>
        ) : null}
        {editing ? (
          <LinkOverlayButton blockId={block.id} index={i} value={image.linkUrl ?? ''} />
        ) : null}
      </div>
      {captionStyle === 'below' && (showCaptions || editing) && (image.caption || editing) ? (
        <figcaption
          className="inline-block min-w-[4rem] px-3 pt-2 text-sm text-muted-foreground empty:before:text-muted-foreground/50 empty:before:content-['Caption']"
          {...(editing ? { 'data-editable': `data.images[${i}].caption` } : {})}
        >
          {image.caption}
        </figcaption>
      ) : null}
      {showButton && image.src && !editing ? (
        <div className="px-3 pb-3 pt-2">
          <a
            href={image.src}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              BUTTON_STYLE_CLASSES[buttonStyle],
            )}
          >
            {buttonText}
          </a>
        </div>
      ) : null}
    </figure>
  );

  if (isMasonry) {
    return (
      <div className={cn('my-8', COLUMN_MASONRY_CLASSES[columns], GAP_CLASSES[gap])}>
        {safeImages.map((image, i) => cellContent(image, i))}
      </div>
    );
  }

  if (isMosaic) {
    return (
      <div className={cn('my-8 grid grid-cols-4 grid-rows-2', GAP_CLASSES[gap])}>
        {safeImages.map((image, i) => {
          const span =
            i === 0
              ? 'col-span-2 row-span-2'
              : i % 5 === 1 || i % 5 === 4
                ? 'col-span-2'
                : 'col-span-1';
          return (
            <div key={image.id} className={span}>
              {cellContent(image, i)}
            </div>
          );
        })}
      </div>
    );
  }

  if (isMarquee) {
    return (
      <div className={cn('my-8 -mx-6 overflow-x-auto')}>
        <div className={cn('flex w-max snap-x snap-mandatory px-6', GAP_CLASSES[gap])}>
          {safeImages.map((image, i) => (
            <div
              key={image.id}
              className={cn('w-[280px] shrink-0 snap-start', isAutoAspect ? '' : 'aspect-square')}
            >
              {cellContent(image, i)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Default: grid
  return (
    <div className={cn('my-8 grid', COLUMN_GRID_CLASSES[columns], GAP_CLASSES[gap])}>
      {safeImages.map((image, i) => (
        <div key={image.id}>{cellContent(image, i)}</div>
      ))}
    </div>
  );
}

function EditableImageCell({
  blockId,
  index,
  src,
  alt,
  isAutoAspect,
}: {
  blockId: string;
  index: number;
  src: string;
  alt: string;
  isAutoAspect: boolean;
}) {
  const updateBlockPath = usePageEditorStore((s) => s.updateBlockPath);
  return (
    <EditableMediaCell
      src={src}
      alt={alt}
      isAutoAspect={isAutoAspect}
      className={isAutoAspect ? 'relative w-full' : 'absolute inset-0 h-full w-full'}
      emptyLabel="Click to add"
      onChange={({ src: nextSrc }) => {
        updateBlockPath(blockId, `data.images[${index}].src`, nextSrc);
      }}
      onRemove={() => updateBlockPath(blockId, `data.images[${index}].src`, '')}
    />
  );
}

function LinkOverlayButton({
  blockId,
  index,
  value,
}: {
  blockId: string;
  index: number;
  value: string;
}) {
  const updateBlockPath = usePageEditorStore((s) => s.updateBlockPath);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const hasValue = value.length > 0;

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) setDraft(value);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={hasValue ? 'Edit image link' : 'Add image link'}
          className={cn(
            'absolute right-2 top-2 z-10 inline-flex h-7 w-7 items-center justify-center rounded-md border bg-background/95 text-muted-foreground shadow-sm transition-opacity hover:text-foreground',
            hasValue ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-2">
          <p className="text-xs font-medium">Link this image to</p>
          <Input
            value={draft}
            placeholder="https://example.com"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                updateBlockPath(blockId, `data.images[${index}].linkUrl`, draft);
                setOpen(false);
              }
            }}
            autoFocus
          />
          <div className="flex justify-between gap-2">
            {hasValue ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  updateBlockPath(blockId, `data.images[${index}].linkUrl`, '');
                  setOpen(false);
                }}
              >
                <Unlink className="mr-1 h-3.5 w-3.5" />
                Remove
              </Button>
            ) : (
              <span />
            )}
            <Button
              size="sm"
              disabled={!draft}
              onClick={() => {
                updateBlockPath(blockId, `data.images[${index}].linkUrl`, draft);
                setOpen(false);
              }}
            >
              {hasValue ? 'Update' : 'Add link'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
